// 🎬 장표 액센트 — 영상의 **처음과 끝에 한 번씩만** 붙는 두 가지(2026-09-17 · 회장님 모션그래픽 레퍼런스 ③).
//
// ★왜 «한 번씩만» 인가★ — 레퍼런스(형광 홍보영상)에서 눈에 남는 두 장면은 ①소실점에서 글자판이 돌진하는
//   도입 ②마지막 카드의 종이 모서리 접힘이었다. 둘 다 **한 영상에 한 번** 나오는 구두점이다. 설계도 가족은
//   강의·공공기관 톤이라 매 씬에 넣으면 시끄럽다 — 그래서 «첫 장표 씬»과 «마지막 장표 씬»에만 건다.
//
// ★렌더 계약★ — **SVG 로만 그린다**. CSS `radial-gradient` 는 다운로드 렌더러(@remotion/web-renderer)가
//   칠하지 않는다(09-17 실측 0픽셀 · AmbientFxLayer 머리말). `linear-gradient` 와 단색은 칠해진다.
// ★결정론★ — 프레임만의 함수. Lambda 가 프레임 구간을 워커로 쪼개므로 난수·상태를 쓰면 청크 경계에서 튄다.
import React from "react";
import { interpolate, Easing } from "remotion";
import { mulberry32 } from "../../lib/mulberry32";

/** 돌진이 끝나는 씬 상대 프레임(30fps 기준 0.8초). 이 뒤로는 장표가 제자리에 선다. */
export const RUSH_FRAMES = 24;
/** 돌진 시작 배율 — 0 에서 시작하면 «점에서 튀어나온다»가 되어 글자가 읽히기 전에 커버린다.
 *  0.72 는 «살짝 뒤에서 다가온다» 정도다(장표는 읽는 화면이라 도입도 절제한다). */
export const RUSH_FROM_SCALE = 0.72;
/** 속도선이 뻗어 나오는 소실점(화면 비율 좌표). 레퍼런스는 좌측 중앙이었다. */
export const RUSH_VANISH = { x: 0.22, y: 0.5 } as const;

/**
 * 🚀 소실점 돌진 — 첫 장표 씬의 도입 0.8초. 장표 덩어리 전체에 거는 transform·opacity.
 * 반환값을 래퍼 style 에 스프레드한다. 끝나면 `scale(1) translate(0,0)` 로 정확히 수렴한다.
 */
export const slideRush = (f: number): { transform: string; opacity: number } => {
  const p = interpolate(f, [0, RUSH_FRAMES], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
  const scale = RUSH_FROM_SCALE + (1 - RUSH_FROM_SCALE) * p;
  // 소실점에서 화면 중앙으로 — 시작 순간 소실점 쪽에 치우쳐 있다가 제자리로 온다.
  const tx = (RUSH_VANISH.x - 0.5) * 100 * (1 - p);
  const ty = (RUSH_VANISH.y - 0.5) * 100 * (1 - p);
  return {
    transform: `translate(${tx.toFixed(3)}%, ${ty.toFixed(3)}%) scale(${scale.toFixed(4)})`,
    opacity: interpolate(p, [0, 0.35], [0, 1], { extrapolateRight: "clamp" }),
  };
};

const LINE_COUNT = 26;

/**
 * 💨 속도선 — 소실점에서 바깥으로 뻗는 가는 선 다발. 돌진과 같은 구간에서만 살고 사라진다.
 * ★SVG `line` + 단색★ — 굵기·길이·투명도만 프레임에 따라 바뀐다(그라디언트 재래스터 없음).
 */
export const SpeedLines: React.FC<{ f: number; color: string; width: number; height: number; seed?: number }> =
  ({ f, color, width, height, seed = 20260917 }) => {
    const p = interpolate(f, [0, RUSH_FRAMES], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
    // 선은 «도입의 기세»다 — 장표가 제자리에 서기 전에 먼저 사라진다(글자 위에 남으면 소음).
    const alpha = interpolate(p, [0, 0.25, 0.75], [0, 0.55, 0], { extrapolateRight: "clamp" });
    if (alpha <= 0.001) return null;
    const r = mulberry32(seed);
    const cx = RUSH_VANISH.x * width;
    const cy = RUSH_VANISH.y * height;
    const reach = Math.hypot(width, height);
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {Array.from({ length: LINE_COUNT }, (_, i) => {
          const ang = r() * Math.PI * 2;
          const near = (0.10 + r() * 0.22) * reach;
          // 길이는 시간에 따라 줄어든다 — 처음엔 길게 뻗고 멈출 때쯤 짧아진다(감속의 시각 신호).
          const len = (0.18 + r() * 0.34) * reach * (1 - p * 0.65);
          const w = 1 + r() * 2.2;
          const dx = Math.cos(ang); const dy = Math.sin(ang);
          return (
            <line key={i}
              x1={cx + dx * near} y1={cy + dy * near}
              x2={cx + dx * (near + len)} y2={cy + dy * (near + len)}
              stroke={color} strokeWidth={w} strokeLinecap="round"
              opacity={alpha * (0.35 + r() * 0.65)} />
          );
        })}
      </svg>
    );
  };

/** 모서리 접힘이 다 펼쳐지는 씬 상대 프레임(30fps 기준 1.2초). */
export const PEEL_FRAMES = 36;
/** 접힌 모서리의 한 변(화면 짧은 쪽 대비). 0.07 = 1080 기준 약 76px. */
export const PEEL_RATIO = 0.07;

/**
 * 📄 모서리 접힘 — 마지막 장표 씬의 우하단. 종이 한 귀퉁이가 들린 것처럼 보이게 한다.
 * ★`linear-gradient` 대신 SVG `linearGradient`★ — CSS 는 칠해지지만 같은 그림을 두 재료로 그리면
 *   나중에 한쪽만 고쳐진다. 이 파일은 SVG 로 통일한다.
 */
export const CornerPeel: React.FC<{ f: number; width: number; height: number; page: string; under: string; edge: string }> =
  ({ f, width, height, page, under, edge }) => {
    const p = interpolate(f, [0, PEEL_FRAMES], [0, 1], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
    });
    if (p <= 0.001) return null;
    const s = Math.min(width, height) * PEEL_RATIO * p;
    // ★id 는 그라디언트를 정하는 전 파라미터에서 결정론 파생★ — 같은 id 면 같은 정의라, 한 문서에
    //   레이어가 여럿 떠도(갤러리·편집 미리보기) `url(#…)` 이 남의 정의를 집어도 무해하다.
    //   색을 빼면 스킨이 다른 두 장표가 같은 id 를 갖고 **남의 색으로 칠해진다**(tokens.tsx SvgRadial 관용구).
    const id = `peel-${page}-${under}-${edge}`.replace(/[^a-zA-Z0-9_-]/g, "x");
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <defs>
          {/* 접힌 면은 **접힌 선 쪽이 빛을 받는다** — 그쪽을 accent, 바깥 끝을 장표 패널색으로.
              ★이 대각 방향이 핵심★ — 양끝을 둘 다 남색 계열로 두면 그려지긴 해도 배경에 묻힌다
              (09-17 실측: 우하단 4.54% 가 바뀌는데 평균 밝기차 0.94 = 눈에 안 보임). */}
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={edge} stopOpacity={0.85} />
            <stop offset="1" stopColor={page} stopOpacity={1} />
          </linearGradient>
        </defs>
        {/* ① 들린 자리 — 그 아래는 그늘이다(배경보다 확실히 어두워야 «들렸다»로 읽힌다) */}
        <path d={`M ${width} ${height - s} L ${width} ${height} L ${width - s} ${height} Z`} fill="#000000" opacity={0.45} />
        {/* ② 말려 올라온 종이면 */}
        <path d={`M ${width} ${height - s} L ${width - s} ${height} L ${width - s * 0.12} ${height - s * 0.12} Z`}
          fill={`url(#${id})`} opacity={0.95} />
        {/* ③ 접힌 선 — 얇은 밝은 선 하나가 «종이»를 만든다 */}
        <line x1={width} y1={height - s} x2={width - s} y2={height}
          stroke={edge} strokeWidth={Math.max(1, s * 0.035)} opacity={0.8} strokeLinecap="round" />
      </svg>
    );
  };
